import React from 'react';
import { ActionIcon, Badge, Group, Indicator, Menu, Text, Tooltip, rem } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCalendar,
  IconPlane,
  IconUserExclamation,
  IconUserOff,
  IconUserPlus,
  IconUserShield,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NotificationGroupEnum } from '@bobex/react-core/domain/NotificationModel/constants/NotificationTypeEnum';
import { NotificationUnreadCountsView } from '@bobex/react-core/domain/NotificationModel/view/NotificationUnreadCountsView';

const ICON_STYLE = { width: rem(18), height: rem(18) };

/**
 * The notifications dropdown of the back-office header: one row per group of the legacy
 * bo_menu_header.jsp, with its unread counter. Every row leads to the same list, as it did there.
 */
export default function NotificationMenu({ counts }: { counts: NotificationUnreadCountsView }) {
  const { t } = useTranslation();

  const groups = [
    {
      key: NotificationGroupEnum.NEW,
      label: t('New'),
      count: counts.newSuppliers,
      color: 'green',
      icon: <IconUserPlus style={ICON_STYLE} stroke={1.5} color="var(--mantine-color-green-6)" />,
    },
    {
      key: NotificationGroupEnum.BLOCKED,
      label: t('Blocked'),
      count: counts.blocked,
      color: 'red',
      icon: <IconUserOff style={ICON_STYLE} stroke={1.5} color="var(--mantine-color-red-6)" />,
    },
    {
      key: NotificationGroupEnum.CAPPED,
      label: t('Capped'),
      count: counts.capped,
      color: 'orange',
      icon: <IconUserShield style={ICON_STYLE} stroke={1.5} color="var(--mantine-color-orange-6)" />,
    },
    {
      key: NotificationGroupEnum.HOLIDAYS,
      label: t('Holidays'),
      count: counts.holidays,
      color: 'yellow',
      icon: <IconPlane style={ICON_STYLE} stroke={1.5} />,
    },
    {
      key: NotificationGroupEnum.MAILING_PLANNINGS,
      label: t('Mailing plannings'),
      count: counts.mailingPlannings,
      color: 'blue',
      icon: <IconCalendar style={ICON_STYLE} stroke={1.5} color="var(--mantine-color-blue-6)" />,
    },
  ];

  return (
    <Menu width={280} position="bottom-end" transitionProps={{ transition: 'pop-top-right' }} withinPortal>
      <Menu.Target>
        <Tooltip label={t('Notifications')}>
          <Indicator
            inline
            label={counts.total}
            size={16}
            color="red"
            disabled={counts.total === 0}
          >
            <ActionIcon variant="default" size="lg" aria-label={t('Notifications')}>
              <IconUserExclamation stroke={1.5} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label ta="center">
          <Group gap={6} justify="center">
            <IconAlertTriangle style={{ width: rem(14), height: rem(14) }} stroke={1.5} />
            {counts.total} {t('Notifications')}
          </Group>
        </Menu.Label>

        {groups.map((group) => (
          <Menu.Item
            key={group.key}
            component={Link}
            to={`/notifications?group=${group.key}`}
            leftSection={group.icon}
            rightSection={
              group.count > 0 ? (
                <Badge size="sm" color={group.color}>
                  +{group.count}
                </Badge>
              ) : null
            }
          >
            {group.label}
          </Menu.Item>
        ))}

        <Menu.Divider />

        <Menu.Item
          component={Link}
          to="/notifications"
          rightSection={<IconArrowRight style={{ width: rem(14), height: rem(14) }} stroke={1.5} />}
        >
          <Text size="sm" fw={600}>
            {t('See all notifications')}
          </Text>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
